import { useState, useEffect } from 'react';
import type { MobileCardField } from '../components/mobile/MobileDataCard';

// Mobile breakpoints
export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
} as const;

// Mobile detection hook
export const useIsMobile = (breakpoint: number = breakpoints.md) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [breakpoint]);

  return isMobile;
};

// Responsive column configuration for Ant Design Grid
export const getResponsiveColumns = () => ({
  xs: 24,
  sm: 12,
  md: 8,
  lg: 6,
  xl: 4,
});

export const getResponsiveColumnsWide = () => ({
  xs: 24,
  sm: 24,
  md: 12,
  lg: 8,
  xl: 6,
});

// Responsive table columns helper
export const getMobileTableColumns = <T>(
  columns: any[],
  isMobile: boolean,
  essentialKeys: string[] = []
) => {
  if (!isMobile) return columns;
  
  return columns.filter(col => 
    essentialKeys.includes(col.key) || 
    col.responsive?.includes('sm') !== false
  );
};

// Mobile-friendly button configuration
export const getMobileButtonProps = (isMobile: boolean) => ({
  size: isMobile ? ('small' as const) : ('middle' as const),
  style: isMobile ? { marginBottom: 8 } : {},
});

// Responsive Space direction
export const getSpaceDirection = (isMobile: boolean) => 
  isMobile ? 'vertical' : 'horizontal';

// Mobile-friendly pagination
export const getMobilePagination = (isMobile: boolean) => ({
  pageSize: isMobile ? 5 : 10,
  showSizeChanger: !isMobile,
  showQuickJumper: !isMobile,
  simple: isMobile,
  size: isMobile ? 'small' : 'default',
});

// Mobile-friendly table props
export const getMobileTableProps = (isMobile: boolean) => ({
  size: isMobile ? ('small' as const) : ('middle' as const),
  scroll: isMobile ? { x: 'max-content' } : undefined,
  pagination: getMobilePagination(isMobile),
});

// Mobile-friendly card props
export const getMobileCardProps = (isMobile: boolean) => ({
  size: isMobile ? ('small' as const) : ('default' as const),
  style: isMobile ? { marginBottom: 12 } : { marginBottom: 16 },
});

// Mobile-friendly form layout
export const getMobileFormLayout = (isMobile: boolean) => ({
  layout: isMobile ? ('vertical' as const) : ('horizontal' as const),
  labelCol: isMobile ? undefined : { span: 6 },
  wrapperCol: isMobile ? undefined : { span: 18 },
});

// Mobile-friendly input sizes
export const getMobileInputProps = (isMobile: boolean) => ({
  size: isMobile ? ('small' as const) : ('middle' as const),
  style: { width: isMobile ? '100%' : undefined },
});

// Mobile-friendly statistic props
export const getMobileStatisticProps = (isMobile: boolean) => ({
  valueStyle: {
    fontSize: isMobile ? '16px' : '24px',
    textAlign: isMobile ? 'center' : 'left',
  },
});

// =====================================================
// Mobile Card Helpers for MobileTableWrapper
// =====================================================

/**
 * Swipe action presets for common operations
 */
export const getSwipeActionPresets = () => ({
  delete: {
    icon: '🗑️',
    color: '#ff4d4f',
    label: 'Delete',
  },
  edit: {
    icon: '✏️',
    color: '#1890ff',
    label: 'Edit',
  },
  view: {
    icon: '👁️',
    color: '#52c41a',
    label: 'View',
  },
  call: {
    icon: '📞',
    color: '#722ed1',
    label: 'Call',
  },
  email: {
    icon: '📧',
    color: '#fa8c16',
    label: 'Email',
  },
  archive: {
    icon: '📦',
    color: '#8c8c8c',
    label: 'Archive',
  },
});

/**
 * Convert Ant Design table columns to mobile card fields
 */
export const tableColumnsToCardFields = <T>(
  columns: Array<{
    key?: string;
    dataIndex?: string | string[];
    title?: React.ReactNode;
    render?: (value: any, record: T, index: number) => React.ReactNode;
  }>,
  options: {
    primary?: string;
    secondary?: string;
    status?: string;
    exclude?: string[];
    include?: string[];
  } = {}
): {
  primaryField: string;
  secondaryField?: string;
  statusField?: string;
  fields: MobileCardField<T>[];
} => {
  const { primary, secondary, status, exclude = [], include } = options;

  // Get key from column
  const getColKey = (col: any): string => {
    if (typeof col.dataIndex === 'string') return col.dataIndex;
    if (Array.isArray(col.dataIndex)) return col.dataIndex.join('.');
    return col.key || '';
  };

  // Filter and map columns
  const eligibleColumns = columns.filter(col => {
    const key = getColKey(col);
    if (!key) return false;
    if (exclude.includes(key)) return false;
    if (include && !include.includes(key)) return false;
    if ([primary, secondary, status, 'actions', 'operation'].includes(key)) return false;
    return true;
  });

  const fields: MobileCardField<T>[] = eligibleColumns.map(col => ({
    key: getColKey(col) as keyof T,
    label: typeof col.title === 'string' ? col.title : undefined,
    render: col.render,
  }));

  return {
    primaryField: primary || getColKey(columns[0]) || 'id',
    secondaryField: secondary,
    statusField: status,
    fields,
  };
};

/**
 * Mobile card presets for common data types
 */
export const getMobileCardPreset = (type: 'lead' | 'client' | 'task' | 'user' | 'company' | 'order' | 'invoice') => {
  const presets: Record<string, any> = {
    lead: {
      primaryField: 'contactName',
      secondaryField: 'companyName',
      statusField: 'status',
      avatar: { icon: '👤' },
      fields: [
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        { key: 'source', label: 'Source' },
      ],
    },
    client: {
      primaryField: 'name',
      secondaryField: 'email',
      statusField: 'status',
      avatar: { icon: '🏢' },
      fields: [
        { key: 'phone', label: 'Phone' },
        { key: 'industry', label: 'Industry' },
        { key: 'createdAt', label: 'Since' },
      ],
    },
    task: {
      primaryField: 'title',
      secondaryField: 'assignee',
      statusField: 'status',
      avatar: { icon: '📋' },
      fields: [
        { key: 'dueDate', label: 'Due' },
        { key: 'priority', label: 'Priority' },
      ],
    },
    user: {
      primaryField: 'name',
      secondaryField: 'email',
      statusField: 'isActive',
      avatar: { icon: '👤' },
      fields: [
        { key: 'role', label: 'Role' },
        { key: 'createdAt', label: 'Joined' },
      ],
    },
    company: {
      primaryField: 'name',
      secondaryField: 'code',
      statusField: 'isActive',
      avatar: { icon: '🏢' },
      fields: [
        { key: 'email', label: 'Email' },
        { key: 'userCount', label: 'Users' },
      ],
    },
    order: {
      primaryField: 'orderNo',
      secondaryField: 'customerName',
      statusField: 'status',
      avatar: { icon: '📦' },
      fields: [
        { key: 'totalAmount', label: 'Amount' },
        { key: 'createdAt', label: 'Date' },
      ],
    },
    invoice: {
      primaryField: 'invoiceNo',
      secondaryField: 'customerName',
      statusField: 'status',
      avatar: { icon: '📄' },
      fields: [
        { key: 'amount', label: 'Amount' },
        { key: 'dueDate', label: 'Due' },
      ],
    },
  };

  return presets[type] || presets.lead;
};

/**
 * Touch-friendly size configurations following Apple HIG and Material Design
 */
export const getMobileTouchTargets = () => ({
  /** Minimum button height (Apple HIG: 44pt) */
  minButtonHeight: 44,
  /** Minimum touch area (Material Design: 48dp) */
  minTouchArea: 48,
  /** Swipe threshold to reveal actions */
  swipeThreshold: 80,
  /** Swipe threshold to trigger action */
  triggerThreshold: 150,
  /** Recommended icon size for touch */
  iconSize: 24,
  /** Recommended spacing between touch targets */
  touchSpacing: 8,
});

/**
 * Get responsive drawer/modal height based on content
 */
export const getMobileDrawerProps = (isMobile: boolean) => ({
  width: isMobile ? '100%' : 600,
  placement: isMobile ? ('bottom' as const) : ('right' as const),
  height: isMobile ? '90vh' : undefined,
});

/**
 * Get responsive modal props
 */
export const getMobileModalProps = (isMobile: boolean) => ({
  width: isMobile ? '100%' : 600,
  centered: true,
  style: isMobile ? { top: 0, padding: 0, maxWidth: '100%' } : undefined,
  styles: isMobile ? {
    body: { maxHeight: '70vh', overflow: 'auto' },
  } : undefined,
});

// Export all utilities
export const ResponsiveUtils = {
  useIsMobile,
  getResponsiveColumns,
  getResponsiveColumnsWide,
  getMobileTableColumns,
  getMobileButtonProps,
  getSpaceDirection,
  getMobilePagination,
  getMobileTableProps,
  getMobileCardProps,
  getMobileFormLayout,
  getMobileInputProps,
  getMobileStatisticProps,
  breakpoints,
  // New mobile card helpers
  getSwipeActionPresets,
  tableColumnsToCardFields,
  getMobileCardPreset,
  getMobileTouchTargets,
  getMobileDrawerProps,
  getMobileModalProps,
}; 